import { Button, Card, Flex, Stack, Table, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import type { Message } from '../../../types/media-sim';

interface MessageListProps {
  messages: Message[];
  onSelect: (id: string) => void;
  onRegister: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCreate: () => void;
}

export function MessageList({
  messages,
  onSelect,
  onRegister,
  onDuplicate,
  onCreate,
}: MessageListProps) {
  const { theme } = useTheme();

  const columns = [
    {
      key: 'message_name',
      header: 'Name',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const msg = row as unknown as Message;
        return (
          <button
            onClick={() => onSelect(msg.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontWeight: 500,
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {msg.message_name || '(Untitled)'}
          </button>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_val: unknown, row: Record<string, unknown>) => (
        <StatusBadge status={(row as unknown as Message).status} />
      ),
    },
    {
      key: 'registered_at',
      header: 'Registered',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const msg = row as unknown as Message;
        return msg.registered_at
          ? new Date(msg.registered_at).toLocaleString()
          : '\u2014';
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const msg = row as unknown as Message;
        return (
          <Flex gap="xs">
            {msg.status === 'draft' && (
              <Button
                variant="outline"
                color="primary"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onRegister(msg.id);
                }}
              >
                Register
              </Button>
            )}
            {msg.status === 'registered' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDuplicate(msg.id);
                }}
              >
                Duplicate
              </Button>
            )}
          </Flex>
        );
      },
    },
  ];

  return (
    <Stack gap="md">
      <Flex justify="end">
        <Button variant="solid" color="primary" onClick={onCreate}>
          Create Message
        </Button>
      </Flex>
      {messages.length === 0 ? (
        <Card variant="outlined">
          <Card.Body>
            <Flex justify="center" style={{ padding: spacing.lg }}>
              <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                No messages yet. Create a message to get started.
              </p>
            </Flex>
          </Card.Body>
        </Card>
      ) : (
        <Table
          data={messages as unknown as Record<string, unknown>[]}
          columns={columns}
        />
      )}
    </Stack>
  );
}
